import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { SupportButton } from '../components/SupportButton';
import { 
  Car, 
  Key, 
  Truck, 
  Wallet, 
  ArrowRight, 
  TrendingUp,
  MapPin,
  Clock,
  Shield
} from 'lucide-react';

const ServiceCard = ({ to, icon: Icon, title, description, color, large }) => (
  <Link 
    to={to}
    className={`card-elevated p-6 flex flex-col justify-between group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
      large ? 'md:col-span-2 md:row-span-2' : ''
    }`}
    data-testid={`service-${title.toLowerCase().replace(' ', '-')}`}
  >
    <div>
      <div 
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <h3 className="text-xl font-bold text-slate-800 font-['Outfit'] mb-2">{title}</h3>
      <p className="text-slate-500 text-sm">{description}</p>
    </div>
    <div className="flex items-center gap-2 mt-4 text-[#00BFA6] opacity-0 group-hover:opacity-100 transition-opacity">
      <span className="text-sm font-medium">Comenzar</span>
      <ArrowRight className="w-4 h-4" />
    </div>
  </Link>
);

const StatCard = ({ icon: Icon, label, value, trend }) => (
  <div className="card-elevated p-4 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center">
      <Icon className="w-6 h-6 text-[#00BFA6]" />
    </div>
    <div>
      <p className="text-slate-500 text-sm">{label}</p>
      <div className="flex items-center gap-2">
        <span className="text-xl font-bold text-slate-800">{value}</span>
        {trend && (
          <span className="text-xs text-green-600 flex items-center">
            <TrendingUp className="w-3 h-3 mr-1" />
            {trend}
          </span>
        )}
      </div>
    </div>
  </div>
);

export default function HomePage() {
  const { user } = useAuth();

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 font-['Outfit'] tracking-tight">
              ¡Hola, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-500 mt-1">¿A dónde vamos hoy?</p>
          </div>
          
          {/* Quick Stats */}
          <div className="flex gap-4 overflow-x-auto pb-2 lg:pb-0">
            <StatCard icon={Wallet} label="Balance" value={`$${user?.wallet_balance?.toFixed(2) || '0.00'}`} />
            <StatCard icon={TrendingUp} label="DIGGII" value={user?.diggii_balance?.toFixed(2) || '0.00'} trend="+2.5%" />
          </div>
        </div>

        {/* Bento Grid Services */}
        <div className="grid md:grid-cols-3 gap-6">
          <ServiceCard
            to="/ride"
            icon={Car}
            title="Viajes"
            description="Solicita un viaje y llega a tu destino de forma segura y rápida"
            color="#00BFA6"
            large
          />
          
          <ServiceCard
            to="/rental"
            icon={Key}
            title="Rentar"
            description="Alquila vehículos por hora, día o semana"
            color="#FFD166"
          />
          
          <ServiceCard
            to="/delivery"
            icon={Truck}
            title="Envíos"
            description="Fletes y mandados a cualquier lugar"
            color="#00887A"
          />
          
          <ServiceCard
            to="/wallet"
            icon={Wallet}
            title="Wallet"
            description="Gestiona tu Digi Wallet y tokens DIGGII"
            color="#FFD166"
          />
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="card-elevated p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6 text-[#00BFA6]" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Tracking en tiempo real</h4>
              <p className="text-slate-500 text-sm">Sigue tu viaje o envío en vivo desde cualquier dispositivo</p>
            </div>
          </div>
          
          <div className="card-elevated p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#FFD166]/10 flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 text-[#FFD166]" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">Disponible 24/7</h4>
              <p className="text-slate-500 text-sm">Servicios disponibles las 24 horas, los 7 días de la semana</p>
            </div>
          </div>
          
          <div className="card-elevated p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00887A]/10 flex items-center justify-center shrink-0">
              <Shield className="w-6 h-6 text-[#00887A]" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">100% Seguro</h4>
              <p className="text-slate-500 text-sm">Conductores verificados y seguimiento GPS en cada viaje</p>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <SupportButton variant="card" />

        {/* Recent Activity Placeholder */}
        <div className="card-elevated p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 font-['Outfit']">Actividad Reciente</h3>
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-[#00BFA6]/10 flex items-center justify-center mx-auto mb-4">
              <Car className="w-8 h-8 text-[#00BFA6]" />
            </div>
            <p className="text-slate-500">No hay actividad reciente</p>
            <p className="text-slate-400 text-sm mt-1">Tus viajes y envíos aparecerán aquí</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
